// frontend/src/components/Profile/PasswordForm.tsx
import React, { useState } from 'react';
import { 
  Lock, 
  Save, 
  X, 
  Eye, 
  EyeOff, 
  CheckCircle, 
  AlertCircle,
  Loader2,
  Shield
} from 'lucide-react';
import { PasswordUpdateData } from './types';

interface PasswordFormProps {
  onUpdate: (data: PasswordUpdateData) => Promise<{success: boolean; message?: string; errors?: any}>;
  isLoading?: boolean;
}

const PasswordForm: React.FC<PasswordFormProps> = ({ 
  onUpdate, 
  isLoading 
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<PasswordUpdateData>({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [errors, setErrors] = useState<any>({});
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setSuccess(false);

    // Client-side validation
    if (formData.new_password !== formData.confirm_password) {
      setErrors({ confirm_password: 'Passwords do not match' });
      return;
    }

    if (formData.new_password.length < 8) {
      setErrors({ new_password: 'Password must be at least 8 characters long' });
      return;
    }

    const result = await onUpdate(formData);
    
    if (result.success) {
      setSuccess(true);
      setIsEditing(false);
      setFormData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      setTimeout(() => setSuccess(false), 3000);
    } else {
      setErrors(result.errors || { general: result.message });
    }
  };

  const handleCancel = () => {
    setFormData({
      current_password: '',
      new_password: '',
      confirm_password: ''
    });
    setIsEditing(false);
    setErrors({});
  };

  const getPasswordStrength = (password: string) => {
    if (password.length === 0) return { strength: 0, label: '', color: '' };
    
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.match(/[a-z]/)) strength++;
    if (password.match(/[A-Z]/)) strength++;
    if (password.match(/[0-9]/)) strength++;
    if (password.match(/[^a-zA-Z0-9]/)) strength++;

    const labels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'];
    const colors = ['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-blue-500', 'bg-green-500'];
    
    return {
      strength: Math.min(strength, 5),
      label: labels[Math.min(strength - 1, 4)] || '',
      color: colors[Math.min(strength - 1, 4)] || 'bg-gray-300'
    };
  };

  const passwordStrength = getPasswordStrength(formData.new_password);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6">
        <div className="mb-2 sm:mb-0">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Password & Security</h2>
          <p className="text-sm text-gray-600 mt-1">Keep your account secure with a strong password</p>
        </div>
        {!isEditing ? (
          <button
            onClick={() => setIsEditing(true)}
            className="flex items-center justify-center px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors border border-blue-200"
          >
            <Lock className="w-4 h-4 mr-2" />
            Change Password
          </button>
        ) : (
          <div className="flex space-x-2">
            <button
              type="button"
              onClick={handleCancel}
              className="flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-lg transition-colors border border-gray-300"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </button>
          </div>
        )}
      </div>

      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
          <div className="flex items-center">
            <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
            <span className="text-sm text-green-800">Password updated successfully!</span>
          </div>
        </div>
      )}

      {errors.general && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <div className="flex items-center">
            <AlertCircle className="w-4 h-4 text-red-500 mr-2" />
            <span className="text-sm text-red-800">{errors.general}</span>
          </div>
        </div>
      )}

      {!isEditing ? (
        <div className="space-y-3">
          <div className="flex items-center text-gray-600">
            <Shield className="w-5 h-5 mr-3" />
            <span>Your password is secure and encrypted</span>
          </div>
          <div className="text-sm text-gray-500">
            <p>• Last updated: Never (or show last update date)</p>
            <p>• Use a strong password with at least 8 characters</p>
            <p>• Include uppercase, lowercase, numbers, and special characters</p>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Current Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Current Password
            </label>
            <div className="relative">
              <input
                type={showPasswords.current ? 'text' : 'password'}
                value={formData.current_password}
                onChange={(e) => setFormData({...formData, current_password: e.target.value})}
                className={`w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.current_password ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter your current password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPasswords({...showPasswords, current: !showPasswords.current})}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showPasswords.current ? (
                  <EyeOff className="w-4 h-4 text-gray-400" />
                ) : (
                  <Eye className="w-4 h-4 text-gray-400" />
                )}
              </button>
            </div>
            {errors.current_password && (
              <p className="mt-1 text-sm text-red-600">{errors.current_password}</p>
            )}
          </div>

          {/* New Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              New Password
            </label>
            <div className="relative">
              <input
                type={showPasswords.new ? 'text' : 'password'}
                value={formData.new_password}
                onChange={(e) => setFormData({...formData, new_password: e.target.value})}
                className={`w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.new_password ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter your new password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPasswords({...showPasswords, new: !showPasswords.new})}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showPasswords.new ? (
                  <EyeOff className="w-4 h-4 text-gray-400" />
                ) : (
                  <Eye className="w-4 h-4 text-gray-400" />
                )}
              </button>
            </div>
            
            {/* Password Strength Indicator */}
            {formData.new_password && (
              <div className="mt-2">
                <div className="flex items-center space-x-2">
                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all duration-300 ${passwordStrength.color}`}
                      style={{ width: `${(passwordStrength.strength / 5) * 100}%` }}
                    />
                  </div>
                  <span className="text-xs text-gray-600 min-w-fit">
                    {passwordStrength.label}
                  </span>
                </div>
              </div>
            )}
            
            {errors.new_password && (
              <p className="mt-1 text-sm text-red-600">{errors.new_password}</p>
            )}
            
            {/* Password Requirements */}
            <div className="mt-2 text-xs text-gray-500 space-y-1">
              <p className="font-medium">Password must contain:</p>
              <ul className="space-y-1">
                <li className={`flex items-center ${formData.new_password.length >= 8 ? 'text-green-600' : 'text-gray-400'}`}>
                  <span className="w-1 h-1 bg-current rounded-full mr-2"></span>
                  At least 8 characters
                </li>
                <li className={`flex items-center ${formData.new_password.match(/[a-z]/) ? 'text-green-600' : 'text-gray-400'}`}>
                  <span className="w-1 h-1 bg-current rounded-full mr-2"></span>
                  One lowercase letter
                </li>
                <li className={`flex items-center ${formData.new_password.match(/[A-Z]/) ? 'text-green-600' : 'text-gray-400'}`}>
                  <span className="w-1 h-1 bg-current rounded-full mr-2"></span>
                  One uppercase letter
                </li>
                <li className={`flex items-center ${formData.new_password.match(/[0-9]/) ? 'text-green-600' : 'text-gray-400'}`}>
                  <span className="w-1 h-1 bg-current rounded-full mr-2"></span>
                  One number
                </li>
                <li className={`flex items-center ${formData.new_password.match(/[^a-zA-Z0-9]/) ? 'text-green-600' : 'text-gray-400'}`}>
                  <span className="w-1 h-1 bg-current rounded-full mr-2"></span>
                  One special character
                </li>
              </ul>
            </div>
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirm New Password
            </label>
            <div className="relative">
              <input
                type={showPasswords.confirm ? 'text' : 'password'}
                value={formData.confirm_password}
                onChange={(e) => setFormData({...formData, confirm_password: e.target.value})}
                className={`w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.confirm_password ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Confirm your new password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPasswords({...showPasswords, confirm: !showPasswords.confirm})}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showPasswords.confirm ? (
                  <EyeOff className="w-4 h-4 text-gray-400" />
                ) : (
                  <Eye className="w-4 h-4 text-gray-400" />
                )}
              </button>
            </div>
            {errors.confirm_password && (
              <p className="mt-1 text-sm text-red-600">{errors.confirm_password}</p>
            )}
            {formData.confirm_password && formData.new_password && (
              <p className={`mt-1 text-xs flex items-center ${
                formData.new_password === formData.confirm_password ? 'text-green-600' : 'text-red-600'
              }`}>
                {formData.new_password === formData.confirm_password ? (
                  <>
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Passwords match
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-3 h-3 mr-1" />
                    Passwords do not match
                  </>
                )}
              </p>
            )}
          </div>

          {/* Submit Button */}
          <div className="pt-4 flex justify-end">
            <button
              type="submit"
              disabled={isLoading || passwordStrength.strength < 3}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Update Password
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default PasswordForm;